import { useState } from 'react'
import Button from '@/components/common/Button'
import Card from '@/components/common/Card'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import CalendarUpload from './CalendarUpload'

/**
 * ClaimTeamFlow Component
 * 
 * Simplified claim flow for scraped/seed teams:
 * - If team is unclaimed: Direct claim with optional calendar verification
 * - If team is already claimed: Request access from current owner
 * 
 * Philosophy: Don't require verification unless there's a conflict.
 * First come, first served for unclaimed teams.
 */
export default function ClaimTeamFlow({ 
  team, 
  onClaimSuccess, 
  onCancel 
}) {
  // 'confirm' = show team & claim button, 'calendar' = optional calendar link, 'success' = done
  const [step, setStep] = useState('confirm')
  const [loading, setLoading] = useState(false)
  const [calendarResult, setCalendarResult] = useState(null)
  const [associationEmail, setAssociationEmail] = useState('')
  const [associationCode, setAssociationCode] = useState('')

  const handleCalendarVerification = async (uploadResult) => {
    setLoading(true)
    try {
      // Compare uploaded calendar URL with team's scraped URL
      const uploadedUrl = uploadResult.calendarUrl || ''
      const scrapedUrl = team.calendar_sync_url || ''
      
      // Normalize URLs for comparison
      const normalizeUrl = (url) => url.toLowerCase().replace(/^webcal:\/\//i, 'https://').replace(/\/$/, '')
      
      const normalizedUploaded = normalizeUrl(uploadedUrl)
      const normalizedScraped = normalizeUrl(scrapedUrl)
      const isMatch = normalizedUploaded && normalizedUploaded === normalizedScraped
      
      if (isMatch) {
        // Calendar URLs match - auto-approve claim
        await claimTeam('calendar_verified', { verifiedCalendarUrl: normalizedUploaded })
      } else {
        // URLs don't match - check for event overlap
        // This would require comparing events, for now we show partial match
        setCalendarResult({
          status: 'partial_match',
          message: 'Calendar URL doesn\'t exactly match our records. You can request manual verification instead.'
        })
      }
    } catch (error) {
      console.error('Calendar verification error:', error)
      toast.error('Failed to verify calendar')
    } finally {
      setLoading(false)
    }
  }

  const submitClaimRequest = async (accessToken, payload) => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/request-team-claim`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify(payload)
      }
    )

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || 'Request failed')
    }
  }

  const handleManualRequest = async () => {
    setLoading(true)
    try {
      // Create a claim request for admin review
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        toast.error('Please sign in to claim a team')
        return
      }

      await submitClaimRequest(session.access_token, {
        teamId: team.id,
        verificationMethod: 'manual'
      })
      toast.success('Claim request submitted! We\'ll review and approve within 24 hours.')
      setStep('pending')
    } catch (error) {
      console.error('Manual request error:', error)
      toast.error('Failed to submit claim request')
    } finally {
      setLoading(false)
    }
  }

  const handleEmailDomainRequest = async () => {
    setLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        toast.error('Please sign in to claim a team')
        return
      }

      const emailToSubmit = (associationEmail || session.user.email || '').trim()
      if (!emailToSubmit) {
        toast.error('Please enter an email address')
        return
      }

      await submitClaimRequest(session.access_token, {
        teamId: team.id,
        verificationMethod: 'email_domain',
        associationEmail: emailToSubmit
      })

      toast.success('Claim request submitted! We\'ll review and approve within 24 hours.')
      setStep('pending')
    } catch (error) {
      console.error('Email-domain request error:', error)
      toast.error('Failed to submit claim request')
    } finally {
      setLoading(false)
    }
  }

  const handleCodeRequest = async () => {
    setLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        toast.error('Please sign in to claim a team')
        return
      }

      const code = (associationCode || '').trim()
      if (!code) {
        toast.error('Please enter the verification code')
        return
      }

      await submitClaimRequest(session.access_token, {
        teamId: team.id,
        verificationMethod: 'code',
        associationCode: code
      })

      toast.success('Claim request submitted! We\'ll review and approve within 24 hours.')
      setStep('pending')
    } catch (error) {
      console.error('Code request error:', error)
      toast.error('Failed to submit claim request')
    } finally {
      setLoading(false)
    }
  }

  const claimTeam = async (verificationMethod, options = {}) => {
    setLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        toast.error('Please sign in to claim a team')
        setLoading(false)
        return
      }

      // Update the team to be claimed
      // NOTE: We do NOT change data_source - it stays 'seed_auto' to indicate origin
      // The claimed_at/claimed_by/user_id fields indicate ownership
      const { error: updateError } = await supabase
        .from('teams')
        .update({
          claimed_at: new Date().toISOString(),
          claimed_by: session.user.id,
          user_id: session.user.id,
          calendar_verified: verificationMethod === 'calendar_verified'
          // data_source stays as-is (seed_auto) to preserve origin
        })
        .eq('id', team.id)
        .is('claimed_at', null) // Only if not already claimed

      if (updateError) {
        console.error('Claim error:', updateError)
        throw new Error('Failed to claim team')
      }

      // Create scrimmage_manager record so contact info shows on bulletin
      // Use the user's email as default contact
      // First check if one exists
      const { data: existingManager } = await supabase
        .from('scrimmage_managers')
        .select('id')
        .eq('team_id', team.id)
        .maybeSingle()

      let managerId = existingManager?.id
      if (!existingManager) {
        const { data: newManager, error: managerError } = await supabase
          .from('scrimmage_managers')
          .insert({
            team_id: team.id,
            user_id: session.user.id,
            name: session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || 'Team Manager',
            email: session.user.email,
            display_email: true,
            display_phone: false
          })
          .select('id')
          .single()

        if (managerError) {
          // Don't fail the claim if manager creation fails - log and continue
          console.warn('Failed to create scrimmage manager record:', managerError)
        } else {
          managerId = newManager?.id
        }
      }

      // Link auto-generated postings to the new scrimmage_manager so contact info shows
      // This is critical - without this, search RPCs won't find contact info
      if (managerId) {
        const { error: linkError } = await supabase
          .from('scrimmage_postings')
          .update({ scrimmage_manager_id: managerId })
          .eq('team_id', team.id)
          .is('scrimmage_manager_id', null)

        if (linkError) {
          console.warn('Failed to link postings to manager:', linkError)
        }
      }

      // Calendar merge: persist the verified calendar to the team's DB calendar (best-effort)
      if (verificationMethod === 'calendar_verified') {
        const calendarUrlToPersist = options.verifiedCalendarUrl || team.calendar_sync_url
        if (calendarUrlToPersist) {
          try {
            await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-team-calendar`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`,
                'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
              },
              body: JSON.stringify({
                teamId: team.id,
                calendarUrl: calendarUrlToPersist,
                calendarType: 'webcal',
                calendarName: 'Verified team calendar'
              })
            })
          } catch (calendarError) {
            // Don't fail the claim if calendar merge fails
            console.warn('Failed to persist calendar during claim:', calendarError)
          }
        }
      }

      // NOTE: We no longer mark auto_generated postings as user-owned
      // They stay auto_generated=true but are now managed by the claimed owner
      // This preserves the "auto-detected" badge on bulletin while giving control

      // Log analytics
      await supabase.from('analytics_events').insert({
        event_name: 'team_claimed',
        user_id: session.user.id,
        team_id: team.id,
        properties: {
          verification_method: verificationMethod,
          team_name: team.name,
          association: team.association_name
        }
      })

      // Send welcome email (fire and forget)
      try {
        const { data: { session: emailSession } } = await supabase.auth.getSession()
        fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-claim-welcome`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(emailSession?.access_token && { 'Authorization': `Bearer ${emailSession.access_token}` }),
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
            },
            body: JSON.stringify({
              teamId: team.id,
              userId: session.user.id
            })
          }
        )
      } catch (emailError) {
        // Don't fail the claim if email fails
        console.warn('Failed to send claim welcome email:', emailError)
      }

      toast.success(`You've claimed ${team.name}!`)
      setStep('success')
      
      if (onClaimSuccess) {
        onClaimSuccess(team)
      }
    } catch (error) {
      console.error('Claim error:', error)
      toast.error(error.message || 'Failed to claim team')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-lg mx-auto">
      {step === 'confirm' && (
        <Card className="p-6">
          <h2 className="font-heading text-2xl font-bold text-brand-charcoal mb-4">
            Claim This Team
          </h2>
          
          <div className="bg-brand-cream rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-brand-charcoal">{team.name}</h3>
            {team.association_name && (
              <p className="text-sm text-brand-slate">{team.association_name}</p>
            )}
            <p className="text-sm text-brand-slate mt-1">
              {team.city}, {team.state} • {team.team_level}
              {team.level_detail && ` ${team.level_detail}`}
            </p>
            {team.calendar_sync_url && (
              <p className="text-xs text-brand-slate mt-2 truncate" title={team.calendar_sync_url}>
                📅 Calendar: {team.calendar_sync_url.substring(0, 50)}...
              </p>
            )}
          </div>

          <p className="text-brand-slate mb-6">
            This team hasn't been claimed yet. You can claim it now and start managing 
            scrimmage postings immediately.
          </p>

          {/* Primary action: Direct claim */}
          <Button
            variant="primary"
            className="w-full mb-4"
            onClick={() => claimTeam('first_claim')}
            loading={loading}
          >
            Claim This Team
          </Button>

          {/* Optional: Link calendar for enhanced features */}
          {team.calendar_sync_url && (
            <div className="border-t border-gray-200 pt-4 mt-4">
              <p className="text-sm text-brand-slate mb-3">
                <strong>Optional:</strong> Link your calendar to enable auto-posting of scrimmage opportunities.
              </p>
              <button
                onClick={() => setStep('calendar')}
                className="w-full p-3 border border-gray-200 rounded-lg hover:border-brand-green transition-colors text-left text-sm"
              >
                <div className="flex items-center gap-2">
                  <span>📅</span>
                  <span className="text-brand-charcoal">Link Calendar After Claiming</span>
                </div>
              </button>
            </div>
          )}

          {onCancel && (
            <div className="mt-6 text-center">
              <button onClick={onCancel} className="text-sm text-brand-slate hover:text-brand-charcoal">
                Cancel
              </button>
            </div>
          )}
        </Card>
      )}

      {step === 'email' && (
        <Card className="p-6">
          <button 
            onClick={() => setStep('confirm')} 
            className="text-sm text-brand-slate hover:text-brand-charcoal mb-4"
          >
            ← Back
          </button>

          <h2 className="font-heading text-xl font-bold text-brand-charcoal mb-2">
            Association Email Verification
          </h2>
          
          <p className="text-sm text-brand-slate mb-4">
            If you have an official association email address, submit it for verification. We'll review and approve your claim.
          </p>

          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-medium text-brand-charcoal">Association Email</span>
              <input
                type="email"
                value={associationEmail}
                onChange={(e) => setAssociationEmail(e.target.value)}
                placeholder="you@association.org"
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </label>

            <Button
              variant="primary"
              className="w-full"
              onClick={handleEmailDomainRequest}
              loading={loading}
            >
              Submit for Review
            </Button>
          </div>
        </Card>
      )}

      {step === 'code' && (
        <Card className="p-6">
          <button 
            onClick={() => setStep('confirm')} 
            className="text-sm text-brand-slate hover:text-brand-charcoal mb-4"
          >
            ← Back
          </button>

          <h2 className="font-heading text-xl font-bold text-brand-charcoal mb-2">
            Association Code Verification
          </h2>
          
          <p className="text-sm text-brand-slate mb-4">
            If your association provided a verification code for this team, enter it below. We'll review and approve your claim.
          </p>

          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-medium text-brand-charcoal">Verification Code</span>
              <input
                type="text"
                value={associationCode}
                onChange={(e) => setAssociationCode(e.target.value)}
                placeholder="Enter code"
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </label>

            <Button
              variant="primary"
              className="w-full"
              onClick={handleCodeRequest}
              loading={loading}
            >
              Submit for Review
            </Button>
          </div>
        </Card>
      )}

      {step === 'calendar' && (
        <Card className="p-6">
          <button 
            onClick={() => setStep('confirm')} 
            className="text-sm text-brand-slate hover:text-brand-charcoal mb-4"
          >
            ← Back
          </button>

          <h2 className="font-heading text-xl font-bold text-brand-charcoal mb-2">
            Calendar Verification
          </h2>
          
          <p className="text-sm text-brand-slate mb-4">
            Upload or paste the calendar URL for <strong>{team.name}</strong>. If it matches 
            the calendar we found ({team.calendar_sync_url?.substring(0, 50)}...), we'll 
            automatically verify your claim.
          </p>

          <CalendarUpload
            mode="public"
            onCalendarUploaded={handleCalendarVerification}
            onCancel={() => setStep('confirm')}
          />

          {calendarResult && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">{calendarResult.message}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setStep('manual')}
              >
                Request Manual Review Instead
              </Button>
            </div>
          )}
        </Card>
      )}

      {step === 'manual' && (
        <Card className="p-6">
          <button 
            onClick={() => setStep('confirm')} 
            className="text-sm text-brand-slate hover:text-brand-charcoal mb-4"
          >
            ← Back
          </button>

          <h2 className="font-heading text-xl font-bold text-brand-charcoal mb-2">
            Manual Verification Request
          </h2>
          
          <p className="text-sm text-brand-slate mb-6">
            We'll review your claim for <strong>{team.name}</strong> and verify your association 
            with the team. This usually takes less than 24 hours.
          </p>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600">
              <strong>What happens next:</strong>
            </p>
            <ul className="text-sm text-gray-600 mt-2 space-y-1">
              <li>• We'll check your email domain against the association</li>
              <li>• We may contact the association to verify</li>
              <li>• You'll receive an email once approved</li>
            </ul>
          </div>

          <Button
            variant="primary"
            className="w-full"
            onClick={handleManualRequest}
            loading={loading}
          >
            Submit Claim Request
          </Button>
        </Card>
      )}

      {step === 'success' && (
        <Card className="p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          <h2 className="font-heading text-xl font-bold text-brand-charcoal mb-2">
            Team Claimed Successfully!
          </h2>
          
          <p className="text-brand-slate mb-6">
            You now manage <strong>{team.name}</strong>. Any auto-generated scrimmage postings 
            have been transferred to your account.
          </p>

          <div className="space-y-3">
            <Button
              variant="primary"
              className="w-full"
              onClick={() => window.location.href = '/scrimmages/manage'}
            >
              Manage Your Scrimmages
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => window.location.href = '/dashboard'}
            >
              Go to Dashboard
            </Button>
          </div>
        </Card>
      )}

      {step === 'pending' && (
        <Card className="p-6 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-blue-700 text-2xl">⏳</span>
          </div>
          
          <h2 className="font-heading text-xl font-bold text-brand-charcoal mb-2">
            Claim Request Submitted
          </h2>
          
          <p className="text-brand-slate mb-6">
            We received your request to claim <strong>{team.name}</strong>. We'll review it and email you once it's approved.
          </p>

          <div className="space-y-3">
            <Button
              variant="primary"
              className="w-full"
              onClick={() => window.location.href = '/scrimmages/bulletin'}
            >
              Back to Scrimmage Board
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => window.location.href = '/dashboard'}
            >
              Go to Dashboard
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
}
